
/**
 * 変更履歴パネルコンポーネント
 * 右側スライドインパネルで変更履歴を表示
 */

import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Collapse,
  Button,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useChangeManager } from '../context/ChangeManagerContext';
import { SOURCE_CONFIG, ENTITY_TYPE_LABELS } from '../constants/fieldLabels';
import { isPusherEnabled } from '../config/backend';

const PANEL_WIDTH = 400;

/**
 * 値を表示用文字列に変換
 * 配列やオブジェクトを安全に文字列化
 */
function formatDisplayValue(value: any): string {
  if (value === null || value === undefined) {
    return '（なし）';
  }
  if (typeof value === 'string') {
    return value || '（なし）';
  }
  if (typeof value === 'boolean') {
    return value ? '✓' : '✗';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '（なし）';
    return value.map((item: any) => {
      if (item === null || item === undefined) return '';
      if (typeof item === 'object') {
        if (item.name) return item.name;
        if (item.label) return item.label;
        if (item.id) return String(item.id);
        try { return JSON.stringify(item); } catch { return '[オブジェクト]'; }
      }
      return String(item);
    }).filter(Boolean).join(', ') || '（なし）';
  }
  if (typeof value === 'object') {
    if (value.name) return value.name;
    if (value.label) return value.label;
    if (value.id) return String(value.id);
    try {
      return JSON.stringify(value);
    } catch {
      return '（オブジェクト）';
    }
  }
  return String(value);
}

/**
 * 変更タイプに応じたアイコンを取得
 */
function getChangeTypeIcon(changeType: string): React.ReactElement {
  switch (changeType) {
    case 'create':
      return <AddCircleOutlineIcon fontSize="small" color="success" />;
    case 'delete':
      return <RemoveCircleOutlineIcon fontSize="small" color="error" />;
    default:
      return <EditIcon fontSize="small" color="primary" />;
  }
}

/**
 * 変更タイプに応じた色を取得
 */
function getChangeTypeColor(changeType: string): string {
  switch (changeType) {
    case 'create': return 'success';
    case 'delete': return 'error';
    default: return 'primary';
  }
}

/**
 * タイムスタンプを相対時間に変換
 */
function formatRelativeTime(timestamp: string | number | Date): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'たった今';
  if (diffMin < 60) return `${diffMin}分前`;
  if (diffHour < 24) return `${diffHour}時間前`;
  if (diffDay < 7) return `${diffDay}日前`;

  return date.toLocaleDateString('ja-JP', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface ChangeDetailItemProps {
  record: any;
  onRevert?: (record: any) => void;
}

/**
 * 変更詳細アイテム
 */
function ChangeDetailItem({ record, onRevert }: ChangeDetailItemProps) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const hasChanges = record.changes && record.changes.length > 0;

  return (
    <Box sx={{ mb: 1 }}>
      <ListItem
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          flexDirection: 'column',
          alignItems: 'stretch',
          p: 1.5,
        }}
      >
        {/* ヘッダー行 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {getChangeTypeIcon(record.changeType)}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {record.summary}
            </Typography>
          </Box>

          {hasChanges && (
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ ml: 'auto' }}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>

        {/* メタ情報行 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={(ENTITY_TYPE_LABELS as any)[record.entityType] || record.entityType}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />

          <Chip
            size="small"
            label={(SOURCE_CONFIG as any)[record.source]?.label || record.source}
            icon={<span style={{ fontSize: '0.7rem' }}>{(SOURCE_CONFIG as any)[record.source]?.icon || '📝'}</span>}
            color={(SOURCE_CONFIG as any)[record.source]?.color || 'default'}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {formatRelativeTime(record.timestamp)}
          </Typography>
        </Box>

        {/* 変更詳細（展開時） */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            {hasChanges ? (
              record.changes.map((change: any, idx: number) => (
                <Box key={idx} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {change.label || change.field}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        textDecoration: 'line-through',
                        color: 'text.disabled',
                        fontSize: '0.8rem',
                      }}
                    >
                      {formatDisplayValue(change.oldValue)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">→</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: 'success.main',
                        fontSize: '0.8rem',
                      }}
                    >
                      {formatDisplayValue(change.newValue)}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                詳細な変更はありません
              </Typography>
            )}

            {/* デバイス情報 */}
            {record.deviceName && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                端末: {record.deviceName}
              </Typography>
            )}

            {/* 復元ボタン（予約の更新のみ） */}
            {onRevert && record.changeType === 'update' && record.entityType === 'reservation' && hasChanges && (
              <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<RestoreIcon fontSize="small" />}
                  onClick={() => onRevert(record)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  復元
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      </ListItem>
    </Box>
  );
}

/**
 * 変更履歴パネル
 */
export function ChangeHistoryPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // ポーリング制御（ローカル state でトグル状態を管理）
  // Pusher を使わないインスタンス（宮古）はポーリング強制ONのためトグル不可
  const pollingForced = !isPusherEnabled();
  const [localPollingOn, setLocalPollingOn] = useState<boolean>(() => {
    if (!isPusherEnabled()) return true; // 強制ON
    try {
      const raw = document.cookie.split('; ').find(c => c.startsWith('calv2_polling_enabled='));
      return raw ? raw.split('=')[1] === 'true' : false;
    } catch { return false; }
  });
  const {
    history,
    historyPanelOpen,
    closeHistoryPanel,
    clearHistory,
    refreshHistory,
    revertChange,
  } = useChangeManager();

  // 復元ダイアログ
  const [revertTarget, setRevertTarget] = useState<any>(null);
  const [isReverting, setIsReverting] = useState(false);

  const handleRevertClick = (record: any) => {
    setRevertTarget(record);
  };

  const handleRevertConfirm = async () => {
    if (!revertTarget) return;
    setIsReverting(true);
    try {
      await revertChange(revertTarget);
      setRevertTarget(null);
      window.alert('復元しました');
    } catch (e: any) {
      window.alert(`復元に失敗しました: ${e.message}`);
    } finally {
      setIsReverting(false);
    }
  };

  // グループ化（日付別）
  const groupedHistory = useMemo(() => {
    const groups: Record<string, any[]> = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    for (const record of history) {
      const date = new Date(record.timestamp);
      date.setHours(0, 0, 0, 0);

      let key: string;
      if (date.getTime() === today.getTime()) {
        key = '今日';
      } else if (date.getTime() === yesterday.getTime()) {
        key = '昨日';
      } else {
        key = date.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric' });
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(record);
    }

    return groups;
  }, [history]);

  const handleClearHistory = () => {
    if (window.confirm('変更履歴をすべて削除しますか？')) {
      clearHistory();
    }
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={historyPanelOpen}
      onClose={closeHistoryPanel}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : PANEL_WIDTH,
          maxWidth: '100vw',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* ヘッダー */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            変更履歴
          </Typography>

          <Tooltip title="更新">
            <IconButton size="small" onClick={refreshHistory} sx={{ mr: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="履歴をクリア">
            <IconButton
              size="small"
              onClick={handleClearHistory}
              disabled={history.length === 0}
              sx={{ mr: 1 }}
            >
              <DeleteSweepIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <IconButton onClick={closeHistoryPanel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 定期更新トグル（Pusher無効インスタンスでは強制ON・変更不可） */}
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="body2" color="text.secondary">
            {pollingForced ? '定期更新（30秒・常時ON）' : '定期更新（30秒）'}
          </Typography>
          <Box
            onClick={() => {
              if (pollingForced) return; // 強制ON中はトグル不可
              const next = !localPollingOn;
              setLocalPollingOn(next);
              // App.js 側のトグルも呼ぶ
              if ((window as any).__pollingControl?.togglePolling) {
                (window as any).__pollingControl.togglePolling();
              }
            }}
            sx={{
              width: 36, height: 20, borderRadius: 10, position: 'relative',
              cursor: pollingForced ? 'default' : 'pointer',
              opacity: pollingForced ? 0.6 : 1,
              bgcolor: localPollingOn ? '#3b82f6' : '#cbd5e1',
              transition: 'background 0.2s',
            }}
          >
            <Box sx={{
              width: 16, height: 16, borderRadius: '50%', bgcolor: '#fff',
              position: 'absolute', top: 2,
              left: localPollingOn ? 18 : 2,
              transition: 'left 0.2s',
            }} />
          </Box>
        </Box>

        {/* コンテンツ */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
          {history.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              変更履歴はまだありません
            </Alert>
          ) : (
            Object.entries(groupedHistory).map(([date, records]) => (
              <Box key={date} sx={{ mb: 2 }}>
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    mb: 1,
                    px: 1,
                  }}
                >
                  {date} ({records.length}件)
                </Typography>

                {records.map((record: any) => (
                  <ChangeDetailItem key={record.id} record={record} onRevert={handleRevertClick} />
                ))}
              </Box>
            ))
          )}
        </Box>

        {/* フッター（統計情報） */}
        {history.length > 0 && (
          <Box
            sx={{
              p: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              合計: {history.length}件 | 保存期間: 7日間
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>

    {/* 復元確認ダイアログ */}
    <Dialog
      open={!!revertTarget}
      onClose={() => !isReverting && setRevertTarget(null)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="warning" />
          変更内容を元に戻しますか？
        </Box>
      </DialogTitle>
      <DialogContent>
        {revertTarget && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {revertTarget.summary}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>フィールド</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>現在の値</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>復元後の値</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revertTarget.changes.map((change: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {change.label || change.field}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'error.main', textDecoration: 'line-through' }}>
                      {formatDisplayValue(change.newValue)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'success.main', fontWeight: 600 }}>
                      {formatDisplayValue(change.oldValue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRevertTarget(null)} disabled={isReverting}>
          いいえ
        </Button>
        <Button
          onClick={handleRevertConfirm}
          variant="contained"
          color="warning"
          disabled={isReverting}
          startIcon={isReverting ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
        >
          {isReverting ? '復元中...' : 'はい、復元する'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default ChangeHistoryPanel;
